import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { 
  CheckSquare, 
  Calendar, 
  User, 
  Building2, 
  Users,
  ArrowRight,
  Filter,
  Search,
  AlertCircle,
  MessageSquare
} from 'lucide-react';

const TaskSelector = ({ onTasksSelected, onClose, draggedTaskIds = [], selectedDate = null, isEditing = false, initialTasks = [] }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [] } = useData();
  
  const [selectedTab, setSelectedTab] = useState('management');
  const [selectedTasks, setSelectedTasks] = useState(initialTasks || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterAssignedToMe, setFilterAssignedToMe] = useState('all'); // all או assigned

  // בדיקה אם כבר קיים תכנון ליום הנבחר (רק אם לא במצב עריכה)
useEffect(() => {
  // דלג על הבדיקה לגמרי אם במצב עריכה
  if (isEditing) return;
  
  if (selectedDate) {
    const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
    const existingPlan = dailyPlans.find(plan => 
      plan.date === selectedDate && 
      (plan.userId === currentUser?.id || plan.userName === currentUser?.name)
    );
    
    if (existingPlan) {
      const dateStr = new Date(selectedDate).toLocaleDateString('he-IL');
      const result = window.confirm(
        `⚠️ כבר קיים תכנון ליום ${dateStr}\n\n` +
        `מה תרצה לעשות?\n\n` +
        `לחץ "אישור" - למחוק את התכנון הקיים ולתכנן מחדש\n` +
        `לחץ "ביטול" - לבחור תאריך אחר`
      );
      
      if (result) {
        // מחיקת התכנון הקיים
        const updatedPlans = dailyPlans.filter(plan => 
          !(plan.date === selectedDate && 
            (plan.userId === currentUser?.id || plan.userName === currentUser?.name))
        );
        localStorage.setItem('dailyPlans', JSON.stringify(updatedPlans));
        alert('✅ התכנון הקודם נמחק. אתה יכול לתכנן מחדש!');
      } else {
        // שינוי לתאריך אחר (מחר)
        const tomorrow = new Date(selectedDate);
        tomorrow.setDate(tomorrow.getDate() + 1);
        // כאן צריך לעדכן את selectedDate ב-parent component
        alert('💡 בחר תאריך אחר לתכנון');
        onClose();
      }
    }
  }
}, [selectedDate, currentUser, isEditing]);

  // פונקציה לקבלת כל המשימות שכבר תוכננו
  const getAlreadyScheduledTaskIds = () => {
    const scheduledIds = new Set();
    
    try {
      // בדיקת תכנונים יומיים
      const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      dailyPlans.forEach(plan => {
        if (plan.schedule) {
          Object.values(plan.schedule).forEach(slot => {
            if (slot?.task?.id) {
              scheduledIds.add(slot.task.id);
            }
          });
        }
      });

      // בדיקת תכנונים שבועיים
      const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      weeklyPlans.forEach(plan => {
        if (plan.summaryData?.schedule) {
          Object.values(plan.summaryData.schedule).forEach(daySchedule => {
            Object.values(daySchedule || {}).forEach(task => {
              if (task?.id) {
                scheduledIds.add(task.id);
              } else if (task?.task?.id) {
                scheduledIds.add(task.task.id);
              }
            });
          });
        }
      });
    } catch (error) {
      console.error('Error loading scheduled tasks:', error);
    }

    // הוספת משימות שנגררו בסשן הנוכחי
    draggedTaskIds.forEach(id => scheduledIds.add(id));

    return scheduledIds;
  };

  // פילטר משימות לפי הרשאות משתמש
  const filterTasksByPermissions = (tasksList) => {
    const scheduledIds = getAlreadyScheduledTaskIds();
    
    // סינון משימות שכבר תוכננו
    let filteredTasks = tasksList.filter(task => !scheduledIds.has(task.id));
    
    // סינון משימות שגרה
    filteredTasks = filteredTasks.filter(task => {
      if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
        return false;
      }
      // בדיקה אם השם מכיל תאריך
      if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
        return false;
      }
      return true;
    });

    // סינון לפי הרשאות המשתמש
    const userRole = currentUser?.role;
    const userId = currentUser?.id;
    const userTeamId = currentUser?.teamId;
    const userDeptId = currentUser?.departmentId;

    switch (userRole) {
      case 'employee':
        // עובד רגיל - רק משימות שלו
        filteredTasks = filteredTasks.filter(task => 
          task.assigneeId === userId ||
          task.creatorId === userId ||
          (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
        );
        break;

      case 'team_manager':
        // מנהל צוות - רק משימות של הצוות שלו
        if (userTeamId) {
          const teamMemberIds = users
            .filter(u => u.teamId === userTeamId)
            .map(u => u.id);
          
          filteredTasks = filteredTasks.filter(task =>
            teamMemberIds.includes(task.assigneeId) ||
            teamMemberIds.includes(task.creatorId) ||
            (Array.isArray(task.assigneeIds) && 
             task.assigneeIds.some(id => teamMemberIds.includes(id))) ||
            task.teamId === userTeamId
          );
        } else {
          // אם אין צוות מוגדר - רק משימות שלו
          filteredTasks = filteredTasks.filter(task => 
            task.assigneeId === userId ||
            task.creatorId === userId
          );
        }
        break;

      case 'dept_manager':
        // מנהל מחלקה - משימות של המחלקה שלו
        if (userDeptId) {
          const deptMemberIds = users
            .filter(u => u.departmentId === userDeptId)
            .map(u => u.id);
          
          filteredTasks = filteredTasks.filter(task =>
            deptMemberIds.includes(task.assigneeId) ||
            deptMemberIds.includes(task.creatorId) ||
            (Array.isArray(task.assigneeIds) && 
             task.assigneeIds.some(id => deptMemberIds.includes(id))) ||
            task.departmentId === userDeptId
          );
        }
        break;

      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        // הנהלה - רואים הכל, אין צורך בסינון
        break;

      default:
        // ברירת מחדל - רק משימות של המשתמש
        filteredTasks = filteredTasks.filter(task => 
          task.assigneeId === userId ||
          task.creatorId === userId
        );
    }

    return filteredTasks;
  };

  // פילטר משימות לפי בחירה
  const getFilteredTasks = () => {
    let filteredTasks = filterTasksByPermissions(tasks);

   if (selectedTab === 'personal') {
  filteredTasks = filteredTasks.filter(task => 
    (task.scope === 'personal' || task.isPersonal === true) &&
    (task.assigneeId === currentUser?.id || 
     task.creatorId === currentUser?.id ||
     (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser?.id)))
  );
} else {
  // משימות ניהוליות - רק משימות שאינן אישיות
  filteredTasks = filteredTasks.filter(task => 
    task.scope !== 'personal' && 
    task.isPersonal !== true
  );
}

    // פילטר לפי חיפוש
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task => 
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // פילטר לפי פרויקט
    if (filterProject) {
      filteredTasks = filteredTasks.filter(task => task.projectId === filterProject);
    }

    // פילטר למשימות שהוקצו אליי בלבד (לא כולל משימות שיצרתי)
    if (filterAssignedToMe === 'assigned') {
      const userId = currentUser?.id;
      filteredTasks = filteredTasks.filter(task => 
        task.assigneeId === userId ||
        (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
      );
    }

    return filteredTasks;
  };

  // עדכון משימות נבחרות כשמשימות נגררות
  useEffect(() => {
    const scheduledIds = getAlreadyScheduledTaskIds();
    setSelectedTasks(prev => 
      prev.filter(task => !scheduledIds.has(task.id))
    );
  }, [draggedTaskIds]);

  const filteredTasks = getFilteredTasks();

  const handleTaskToggle = (task) => {
    setSelectedTasks(prev => {
      const isSelected = prev.some(t => t.id === task.id);
      if (isSelected) {
        return prev.filter(t => t.id !== task.id);
      } else {
        return [...prev, task];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedTasks([...filteredTasks]);
  };

  const handleDeselectAll = () => {
    setSelectedTasks([]);
  };

  const handleConfirmSelection = () => {
    // עבור ישירות לתכנון זמנים ללא שלב הערות
    onTasksSelected(selectedTasks, selectedTab, '');
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'ללא פרויקט';
  };

  const getAssigneeName = (assigneeId) => {
    const user = users.find(u => u.id === assigneeId);
    return user ? user.name : 'לא מוקצה';
  };

  const getPermissionMessage = () => {
    const role = currentUser?.role;
    const teamName = currentUser?.teamName;
    const deptName = currentUser?.departmentName;

    switch (role) {
      case 'team_manager':
        return `מוצגות משימות של צוות: ${teamName || 'לא מוגדר'}`;
      case 'dept_manager':
        return `מוצגות משימות של מחלקת: ${deptName || 'לא מוגדר'}`;
      case 'employee':
        return 'מוצגות המשימות שלך בלבד';
      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        return 'מוצגות כל המשימות בארגון';
      default:
        return 'מוצגות משימות לפי הרשאות';
    }
  };

  const scheduledCount = getAlreadyScheduledTaskIds().size;

  return (
    <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 max-w-5xl mx-auto">
      {/* Header */}
      <div className="p-8 border-b border-gray-100/50 bg-gradient-to-r from-blue-50/50 to-purple-50/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              בחר משימות לתכנון היום
            </h2>
            <p className="text-gray-600 mt-2 text-lg">
              בחר את המשימות שתרצה לתכנן ליום שלך בצורה מושלמת ✨
            </p>
            {selectedDate && (
              <p className="text-sm text-gray-500 mt-1">
                תאריך: {new Date(selectedDate).toLocaleDateString('he-IL')}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100/80 rounded-2xl transition-all duration-200 hover:scale-105"
          >
            <span className="text-gray-500 text-xl">✕</span>
          </button>
        </div>
      </div>

      {/* Alerts */}
      <div className="p-6 space-y-3 bg-gray-50 border-b">
        {/* הודעת הרשאות */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-blue-800 font-medium">
              {getPermissionMessage()}
            </span>
          </div>
        </div>

        {/* הודעה על משימות שכבר תוכננו */}
        {scheduledCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                {scheduledCount} משימות כבר תוכננו ולא יופיעו ברשימה
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="p-8 border-b border-gray-100/50">
        <div className="flex space-x-2 bg-gray-100/80 p-2 rounded-2xl backdrop-blur-sm">
          <button
            onClick={() => setSelectedTab('management')}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
              selectedTab === 'management'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <Building2 className="w-5 h-5 inline mr-3" />
            משימות ניהוליות
          </button>
          <button
            onClick={() => setSelectedTab('personal')}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
              selectedTab === 'personal'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <User className="w-5 h-5 inline mr-3" />
            משימות אישיות
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 border-b bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Assigned To Me Filter - NEW! */}
          {(currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') && (
            <select
              value={filterAssignedToMe}
              onChange={(e) => setFilterAssignedToMe(e.target.value)}
              className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-purple-50 to-pink-50 font-semibold"
            >
              <option value="all">🌐 כל המשימות</option>
              <option value="assigned">👤 משימות שהוקצו אליי</option>
            </select>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="חיפוש משימות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Project Filter */}
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">כל הפרויקטים</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tasks List */}
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              {selectedTab === 'management' ? 'משימות ניהוליות' : 'משימות אישיות'}
            </h3>
            <span className="text-sm text-gray-600">
              {filteredTasks.length} משימות זמינות
            </span>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              disabled={filteredTasks.length === 0}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all text-sm font-medium"
            >
              ✓ בחר הכל
            </button>
            <button
              onClick={handleDeselectAll}
              disabled={selectedTasks.length === 0}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all text-sm font-medium"
            >
              ✕ בטל הכל
            </button>
          </div>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredTasks.map(task => {
            const isSelected = selectedTasks.some(t => t.id === task.id);
            const project = projects.find(p => p.id === task.projectId);
            
            return (
              <div
                key={task.id}
                onClick={() => handleTaskToggle(task)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTaskToggle(task)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <h4 className="font-medium text-gray-900">{task.title}</h4>
                    </div>
                    
                    {task.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {task.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {/* Project */}
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        <span className="font-medium text-blue-600">
                          {project ? project.name : 'ללא פרויקט'}
                        </span>
                      </div>

                      {/* Assignee */}
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>{getAssigneeName(task.assigneeId)}</span>
                      </div>

                      {/* Due Date */}
                      {task.dueDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(task.dueDate).toLocaleDateString('he-IL')}</span>
                        </div>
                      )}

                      {/* Priority */}
                      {task.priority && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority === 'high' ? 'גבוהה' :
                           task.priority === 'medium' ? 'בינונית' : 'נמוכה'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">לא נמצאו משימות זמינות</p>
            <p className="text-sm mt-2">
              {scheduledCount > 0 
                ? 'ייתכן שכל המשימות כבר תוכננו'
                : 'אין משימות התואמות את ההרשאות שלך'
              }
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedTasks.length} משימות נבחרו
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              ביטול
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={selectedTasks.length === 0}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl hover:from-blue-600 hover:to-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-3 font-semibold shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100"
            >
              <ArrowRight className="w-5 h-5" />
              המשך לתכנון זמנים
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskSelector;